/**
 * How a sign-out reaches this browser's other tabs: a message on a BroadcastChannel, or, where the
 * browser has none (Safari before 15.4), a localStorage write, which every other tab of the origin
 * hears as a `storage` event. Each tab answers it by ending its own part of the session
 * (`utils/logout.ts`). Either way a tab never hears what it said itself: a channel object does not
 * receive its own messages, so this Document listens and speaks on one, and a `storage` event
 * never fires in the tab that wrote.
 */
const CHANNEL_NAME = "primepoint:sign-out";
const FALLBACK_KEY = "pp.signedOut";
const SIGNED_OUT = "signed-out";

/** This Document's channel, opened by `listenForSignOut`; null where the browser has none. */
let channel: BroadcastChannel | null = null;

function openChannel(): BroadcastChannel | null {
    if (typeof BroadcastChannel === "undefined") return null;
    try {
        return new BroadcastChannel(CHANNEL_NAME);
    } catch {
        // An opaque origin may refuse one; the storage event still carries the word.
        return null;
    }
}

/** Tells this browser's other tabs that the person signed out. */
export function announceSignOut(): void {
    const speaker = channel ?? openChannel();
    if (speaker) {
        speaker.postMessage({ kind: SIGNED_OUT });
        if (speaker !== channel) speaker.close();
        return;
    }
    try {
        localStorage.setItem(FALLBACK_KEY, `${Date.now()}:${Math.random()}`);
        localStorage.removeItem(FALLBACK_KEY);
    } catch {
        // Storage refused: the other tabs learn of it at their next request, as before.
    }
}

/** In every tab: `onSignOut` runs when another tab of this browser signs out. Returns how to stop. */
export function listenForSignOut(onSignOut: () => void): () => void {
    const listening = openChannel();
    if (listening) {
        channel = listening;
        listening.onmessage = (event: MessageEvent) => {
            if ((event.data as { kind?: unknown } | null)?.kind === SIGNED_OUT) onSignOut();
        };
        return () => {
            listening.close();
            if (channel === listening) channel = null;
        };
    }
    const onStorage = (event: StorageEvent) => {
        if (event.key === FALLBACK_KEY && event.newValue !== null) onSignOut();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
}
